import {
  ApolloClient,
  ApolloLink,
  fromPromise,
  HttpLink,
  ServerError
} from '@apollo/client'
import { InMemoryCache, NormalizedCacheObject } from '@apollo/client/cache'
import { setContext } from '@apollo/client/link/context'
import { ErrorLink, onError } from '@apollo/client/link/error'
import { createUploadLink } from 'apollo-upload-client'
import { JWTTokenKey, refreshTokenKey } from 'context/AuthContext/UserSession'
import { MAINTENANCE_MODE_KEY } from 'context/MaintenanceContext'
import FastMutex from 'fast-mutex'

import possibleTypes from '../__generated__/possibleTypes.json'

export interface ErrorWithStatus extends Error {
  status: number
}

const mutex = new FastMutex()

const URL = import.meta.env.VITE_REACT_APP_PUBLIC_GRAPHQL_ENDPOINT

type Resolve = () => void
let isRefreshingJWT = false
let pendingRequests: Resolve[] = []
let jwtToken: string | undefined

const resolvePendingRequests = () => {
  console.log('resolvePendingRequests')
  // pendingRequests.map((resolve) => resolve())
  pendingRequests = []
}

const jwtRefreshEndpoint =
  URL?.split('/').slice(0, -1).join('/') + '/refresh-token'
const mutexSessionIdKey = 'sessionIdInternal'

export const getJwtFromRefreshToken = async (): Promise<string | undefined> => {
  console.log(' * refresh JWT: start')
  try {
    await mutex.lock(mutexSessionIdKey)
    const refreshToken = localStorage.getItem(refreshTokenKey)

    const response: any = await fetch(jwtRefreshEndpoint, {
      headers: {
        'x-jwt-refresh-token': refreshToken ?? ''
      },
      method: 'POST',
      mode: 'cors',
      credentials: 'include'
    })

    if (response.ok) {
      const body = await response.json()
      localStorage.setItem(refreshTokenKey, body?.jwt_refresh_token)
      localStorage.setItem(JWTTokenKey, body?.jwt)
      jwtToken = body?.jwt
      return body?.jwt
    } else {
      const err: ErrorWithStatus = new Error(
        `Refresh JWT: ${response.status}`
      ) as ErrorWithStatus
      err.status = response.status
      throw err
    }
  } catch (err) {
    console.error('Refresh JWT error:', err)
    throw err
  } finally {
    mutex.release(mutexSessionIdKey)
  }
}

let logoutCallback: () => void

export const setLogoutCallback = (callback: () => void) => {
  logoutCallback = callback
}

export const setJWT = (jwt: string | undefined) => {
  jwtToken = jwt
}

export function createApolloClient(): ApolloClient<NormalizedCacheObject> {
  const link = ApolloLink.split(
    (operation) => operation.getContext().hasUpload,
    createUploadLink({
      uri: URL,
      credentials: 'include'
    }),
    new HttpLink({
      uri: URL,
      credentials: 'include'
    })
  )

  const authHeaderLink = setContext((_, { headers }) => {
    const authorization = () =>
      jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {}
    return {
      headers: {
        ...headers,
        ...authorization()
      }
    }
  })

  const onErrorCallback: ErrorLink.ErrorHandler = ({
    graphQLErrors,
    networkError,
    operation,
    forward
  }) => {
    console.log(' * Apollo client onError')

    if (networkError) {
      const serverError = networkError as ServerError
      const statusCode = serverError.statusCode

      if (statusCode === 503 || statusCode === 504) {
        localStorage.setItem(MAINTENANCE_MODE_KEY, 'true')
      } else if (statusCode === 204 || statusCode >= 400) {
        console.log(`[Network error]: ${serverError.response.status}`)
      }

      // Check error on installation not found
      if (
        statusCode === 500 &&
        serverError.result &&
        typeof serverError.result !== 'string' &&
        serverError.result.error?.message.includes('installation')
      ) {
        console.log('[Network Error: 500]', serverError.result.error?.message)
        if (logoutCallback) {
          logoutCallback()
        }
      }
    } else {
      console.log('Network error is null')
    }

    if (graphQLErrors && Array.isArray(graphQLErrors)) {
      console.log('[GraphQL error]:', graphQLErrors)

      for (const error of graphQLErrors) {
        const { message, locations, path } = error

        if (message === 'JWTExpired') {
          console.log(' * JWT expired, refresh token:')

          let _forward
          if (isRefreshingJWT) {
            // It will resolve only when we call pendingRequests
            _forward = fromPromise(
              new Promise<void>((resolve) => {
                pendingRequests.push(() => resolve())
              })
            )
          } else {
            isRefreshingJWT = true

            _forward = fromPromise(
              getJwtFromRefreshToken()
                .then((jwt) => {
                  jwtToken = jwt
                  const { headers } = operation.getContext()
                  operation.setContext({
                    headers: {
                      ...headers,
                      Authorization: jwt ? `Bearer ${jwt}` : ''
                    }
                  })

                  isRefreshingJWT = false
                  resolvePendingRequests()
                  return jwt
                })
                .catch((error) => {
                  jwtToken = undefined

                  console.log(' * Error refresh JWT:', error)
                  isRefreshingJWT = false
                  pendingRequests = []

                  if (logoutCallback) {
                    logoutCallback()
                  }
                })
                .finally(() => {
                  console.log(' * Refresh JWT done!')
                })
            ).filter((v) => Boolean(v))
          }

          return _forward.flatMap(() => {
            // retry the request, returning the new observable
            console.log(' * Forwarding operation...')
            return forward(operation)
          })
        }

        if (message === 'Invalid JWT') {
          console.log(' * Invalid JWT, clean session:')

          jwtToken = undefined
          if (logoutCallback) {
            logoutCallback()
          }
        }

        const m = `Message: ${message}`
        const l = `Location: ${JSON.stringify(locations)}`
        const p = `Path: ${path}`
        console.log(`[GraphQL error]: ${m}, ${l}, ${p}`)
      }
    }
  }

  const errorLink = onError(onErrorCallback)

  // https://www.apollographql.com/docs/react/data/fragments/#generating-possibletypes-automatically
  const cache = new InMemoryCache({
    possibleTypes,
    typePolicies: {
      FreeTimeslot: {
        keyFields: ['id', 'startAt', 'endAt']
      },
      Visit: {
        keyFields: ['id', 'serviceDate']
      }
    }
  })

  return new ApolloClient({
    ssrMode: typeof window === 'undefined',
    link: ApolloLink.from([authHeaderLink, errorLink, link]),
    cache
  })
}

export const apolloClient = createApolloClient()
